import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const CareersHub = () => {
  const phrases = [
    "Impactful Engineering Engineering Paths",
    "Dynamic Distributed Culture Hubs"
  ];

  const cardData = [
    { type: 'purple-clay', content: '🚀' },
    { type: 'smooth-wave', content: '💻' },
    { type: 'abstract-blocks', content: '🌍' },
    { type: 'pink-sphere', content: '📈' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🚀", title: "Accelerated Professional Paths", desc: "Access transparent promotion frameworks, ongoing management training, and direct path mentorship plans across our engineering groups." },
    { icon: "💻", title: "Modern Technical Stack", desc: "Build systems utilizing next-generation cloud architectures, optimized automated pipelines, and highly available edge data grids." },
    { icon: "🌍", title: "Distributed Working Autonomy", desc: "Work fluidly from anywhere with structured async communication loops, stipend allowances for home offices, and core timezone flexibility." },
    { icon: "🧠", title: "Continuous Technical Upskilling", desc: "Utilize dedicated annual learning stipends, structural tuition matching parameters, and internally managed tech talk tracks." },
    { icon: "📋", title: "Holistic Family Benefits", desc: "Access comprehensive health coverages, flexible parental leaves, mental wellness support nodes, and matching retirement programs." },
    { icon: "🔄", title: "Internal Mobility Tracks", desc: "Transition cleanly between different engineering teams or product squads through open internal applications and training support." },
    { icon: "🛡️", title: "Inclusive Workplace Guardrails", desc: "Engage with supportive Employee Resource Groups, regular dynamic inclusion training loops, and blind evaluation patterns." }
  ];

  const advancedServices = [
    { icon: "📈", title: "Equity & Performance Incentives", desc: "Transform individual engineering impacts directly into wealth opportunities via regular performance bonuses and stock allocation models." },
    { icon: "👥", title: "High-Caliber Technical Peers", desc: "Collaborate inside cross-functional units staffed explicitly with senior domain experts and creative product managers." },
    { icon: "🌍", title: "Global Integration Offsites", desc: "Gather regularly for international product hackathons, group engineering alignment weeks, and systemic planning workshops." },
    { icon: "🗂️", title: "Open-Source Innovation Hubs", desc: "Contribute to company-backed public code repositories or spend dedicated research sprints working on independent features." },
    { icon: "🎯", title: "High-Impact Product Scope", desc: "Accelerate features that directly touch millions of active production clients using real-time scale analytics tools." },
    { icon: "🔧", title: "Modern Tooling Foundations", desc: "Combine premier workstation provisions with complete enterprise access licenses for top performance monitoring utilities." },
    { icon: "⚖️", title: "Sustainable Velocity Shields", desc: "Enforce healthy work life separation metrics with zero weekend deploy expectations and objective sprint planning buffers." },
    { icon: "🛣️", title: "Technical Leadership Tracks", desc: "Advance along dedicated individual contributor pathways matching management tiers without leaving active development." },
    { icon: "🧪", title: "Research & Development Pods", desc: "Conduct blue-sky prototyping loops designed explicitly to challenge structural limits and pilot next-generation product blocks." }
  ];

  const technologies = {
    "Engineering Languages Stack": ["TypeScript / Rust Nodes", "Golang Concurrent Networks", "Python Machine Learning Blocks", "Modern C++ Core Frameworks"],
    "Cloud & Infrastructure Hubs": ["AWS Cloud Orchestrations", "Kubernetes Native Pods", "Terraform Multi-Cloud Stacks", "Google Cloud Storage Matrices"],
    "Data & Pipeline Architectures": ["PostgreSQL Advanced Engines", "Kafka Streaming Telemetry", "Redis Caching Topologies", "Snowflake Analytics Warehouses"],
    "Frontend & Product Systems": ["React Next.js Architectures", "GraphQL Data Aggregations", "Tailwind CSS Design Packages", "WebAssembly High Performance Layers"],
    "Collaboration Frameworks": ["GitHub Enterprise Sync", "Linear Agile Operations", "Slack Async Communications", "Notion Corporate Knowledge Bases"]
  };

  const processSteps = [
    { num: "01", title: "Application Ingestion", desc: "Submitting your technical profile matrix, reviewing match options, and capturing background focus limits." },
    { num: "02", title: "Technical Skill Screening", desc: "Completing a practical take-home scenario, live pair-programming challenge, or system engineering evaluation loop." },
    { num: "03", title: "Architecture Panel Review", desc: "Engaging with cross-functional technical teams to discuss scaling patterns, tool applications, and collaborative design parameters." },
    { num: "04", title: "Values & Culture Dialogue", desc: "Reviewing execution styles, alignment strategies with async communication rules, and cross-team empathy parameters." },
    { num: "05", title: "Offer Delivery Modeling", desc: "Structuring competitive base parameters, comprehensive equity vest choices, and regional medical benefit packages." },
    { num: "06", title: "Structured Onboarding Run", desc: "Scaling context through technical bootcamps, architecture deep-dives, and shipping production code within week two." },
    { num: "07", title: "Continuous Progress Mapping", desc: "Conducting regular career progression checkpoints, identifying skill expansion goals, and mapping internal promotion milestones." }
  ];

  const industries = [
    "Distributed Software Engineering", "Cloud Infrastructure Groups", "Data Science & Analytics Systems", "Product Design Networks",
    "Machine Learning Operations Labs", "Technical Operations Hubs", "Product Management Chapters", "Cybersecurity Shield Squads",
    "Solutions Architecture Fields", "Developer Relations Platforms", "Corporate Operations Networks", "Creative Media Design Studios"
  ];

  const designBenefits = [
    "Optimized Workplace Comforts", "Elastic Learning Resource Models", "Strict Pay Equity Protections",
    "Continuous Skill Path Tracking", "Sharpened Focus on Technical Impact", "Automated Device Provisioning Cycles",
    "Enhanced Professional Job Satisfaction", "Standardized Equal Opportunity Paradigms"
  ];

  const whyChooseUs = [
    "Certified Great Place to Work", "State-of-the-Art Internal Tooling", "Uncompromising Employee Privacy Protections",
    "Flawless Leadership Strategy Execution", "Deep Industry-Leading Product Sourcing", "Flexible Resource Working Arrangements",
    "Data-Driven Growth Career Pathways", "Completely Transparent Compensation Frameworks", "Proven Work Life Harmony Systems",
    "Dedicated Engineering Mentorship Pods", "Modern Distributed Team Architectures", "100% Inclusive Workplace Safety Guarantee"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Careers Hub | Join Our Team & Explore IT Career Opportunities | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Careers Hub | Join Our Team & Explore IT Career Opportunities | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Explore exciting career opportunities at Affsoft IT Solutions Pvt. Ltd. Join our team of talented professionals and build your career in software development, ERP consulting, cloud technologies, digital transformation, AI, and enterprise solutions."
        />

        <meta
          name="keywords"
          content="Careers Hub, IT Jobs, Software Developer Jobs, ERP Jobs, NetSuite Careers, Cloud Computing Jobs, AI Jobs, Digital Transformation Careers, Full Stack Developer Jobs, React Developer Jobs, Node.js Jobs, Enterprise Software Careers, Technology Careers, IT Recruitment, Join Affsoft, Affsoft Careers, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/careers-hub"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Careers Hub | Join Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Start your career with Affsoft IT Solutions Pvt. Ltd. Explore opportunities in software engineering, ERP consulting, cloud computing, AI, enterprise applications, and digital transformation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/careers-hub"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Careers Hub | IT Jobs & Career Opportunities | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Discover rewarding career opportunities at Affsoft IT Solutions. Join our growing team and work on enterprise software, cloud, ERP, AI, and digital transformation projects."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="process-outsourcing-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f172a' // dark slate career workspace fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-info fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Careers Hub</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At High Tech Solutions, we transform standard tech roles into exceptional professional journeys and high-impact engineering careers. Our advanced workplace frameworks blend highly collaborative engineering squads, modern technical toolstacks, and structural path models to empower individual contributors and scale global platforms.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>View Open Positions</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-info fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase">Culture & Growth</span>
              <h2 className="fw-bold text-dark display-6">Workplace Systems Tailored for Continuous Innovation</h2>
              <p className="text-muted mt-3">
                Remaining stuck in stagnant development paradigms limits potential and stalls impact. At High Tech Solutions, we apply supportive organizational frameworks to protect product focus and accelerate your career milestones.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-info">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Operational Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Step into Our Engineering Ecosystem?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-info fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED BUSINESS SYSTEMS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-dark text-white px-3 py-2 rounded-pill mb-2 text-uppercase">Total Rewards</span>
              <h2 className="fw-bold text-dark display-6">Performance Standards Backed by Real Value Creation</h2>
              <p className="text-muted mt-3">
                We leverage premium corporate frameworks to add stability and protection to your career journey. From comprehensive mental wellness nodes to open development research sprints, our structures offer stable operational backing built to empower engineering brilliance.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-dark">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Our Engineering Environments</span>
              <h2 className="fw-bold display-6">Technical Tools & Platforms We Deploy Daily</h2>
              <p className="text-light-50">We run complex global engineering tasks using cutting edge production stacks.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-info mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-info opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">The Interview Path</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Interview Lifecycle</h2>
              <p className="text-muted">A clear, candid interview roadmap designed to assess capability transparently and respect candidate timelines.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-info border-4 h-100 shadow-sm">
                    <div className="fw-bold text-info display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Chapters & Disciplines Hiring Now</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY PARTNER WITH US ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Grow Your Skills with High Tech Solutions?</h2>
              <p className="text-muted">We combine stable business scaling with intentional career architecture to foster true developer autonomy.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-info fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default CareersHub;